import {
    Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output,
} from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, first, map, switchMap, take } from 'rxjs/operators';

import {
    ApiSizeInfo,
    CartConstants,
    CartItem,
    CartItemImg,
    CartItemOptions,
    CartItemPrices,
    CartItemTypes,
    CartProductTypes,
} from '../../../idt-ecommerce-api/cart/cart.models';
import { ShopCartService } from '../services/shop-cart.service';
import { UNAVAILABLE_ITEM } from '../shop-cart/shop-cart.models';
import { getShopCartItemSizesFactory, getShopCartItemsStocks } from '../store/shopcart.selectors';

import {
    ItemProductCartButtonActions,
    ItemType,
    ItemTypeLegacy,
    OPTIONS_CART,
    OPTIONS_CART_BUYLATER,
    OPTIONS_CART_WISH,
    OPTIONS_DEFAULT,
    OPTIONS_WISH,
} from './item-product-cart.model';
import { ItemProductCartService } from './item-product-cart.service';
import { QuantitySizeOptions } from './quantity-size-selector/quantity-size-selector.component';

import { lowThreshold, Product } from '@api/catalog/products/products.models';
import { ProductsService } from '@api/catalog/products/products.service';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { DatesService } from '@core/services/date/dates.use-cases';
import { StaticResourceService } from '@core/services/static-resource.service';
import { TranslationService } from '@core/services/translation.service';
import { UiService } from '@core/services/ui.service';
import { isDefined } from '@frontend/common/utils';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { GA4EventType } from '@shared/analytics/GA4/models/general.model';
import { GA4AnalyticsPublicActions } from '@shared/analytics/GA4/public.api';
import { LoadCompositionModal, LoadNoStockModal } from '@shared/product/store';

@Component({
    selector: 'item-product-cart',
    templateUrl: './item-product-cart.component.html',
    styleUrls: ['./item-product-cart.component.scss'],
    // Este componente tiene que ser standalone?
    // standalone: true,
    // imports: [
    //     CommonModule,
    //     TranslateModule,
    //     PricesModule,
    //     ProductCommonModule,
    //     UiSvgIconModule,
    //     ShippingModule
    // ],
})
export class ItemProductCartComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public item: CartItem;

    @Input() public type: CartItemOptions = { name: CartItemTypes.ITEM_DEFAULT };

    @Input() public enableComposition: boolean = false;

    @Output() public readonly selectAction: EventEmitter<{ action: string; item: CartItem }> = new EventEmitter();

    @Output() public readonly selectSize: EventEmitter<{ item: CartItem; size: ApiSizeInfo }> = new EventEmitter();

    @Output() public readonly selectQuantity: EventEmitter<{ item: CartItem; quantity: number }> = new EventEmitter();

    private readonly onDestroy$ = new Subject<void>();

    private readonly itemTypeEditable = ItemType.PRODUCT || ItemTypeLegacy.CLOTHES;

    public hasStockAllSizes: boolean;

    public hasStock: boolean;

    public isCustomization: boolean = false;

    public isGift: boolean = false;

    public isPatch: boolean = false;

    public isCard: boolean = false;

    public showColor: boolean = true;

    public showColorName: boolean = false;

    public showSize: boolean = true;

    public showQuantity: boolean = true;

    public showActions: boolean = true;

    public showDisplayReference: boolean = true;

    public showReference: boolean = false;

    public showQuantityEdit: boolean = false;

    public showSizeEdit: boolean = false;

    public showMessageStock: boolean = false;

    public enableClick: boolean = true;

    public isEditable: boolean;

    public quantitySelected: QuantitySizeOptions;

    public quantityOptions: QuantitySizeOptions[];

    public selectedSize: QuantitySizeOptions;

    public optionsSizes$: Observable<QuantitySizeOptions[]>;

    public oneSize: boolean = false;

    public sizeName: string = '';

    public colorName: string = '';

    public inactiveClass: string = '';

    public showStockOrigin$: Observable<boolean>;

    public preventMultiplyQuanity: boolean = false;

    public unavailableItem = UNAVAILABLE_ITEM;

    public itemImagesUrl$: Observable<CartItemImg>;

    public itemPrices$: Observable<CartItemPrices>;

    public actions$: Observable<ItemProductCartButtonActions[]>;

    public showPreSaleKey: boolean = false;

    public isLastUnits$: Observable<boolean>;

    public giftImage: string = this.staticResourceService.getImage('/giftTicket/gift-box.jpg', true);

    public showDate = false;

    public date: string;

    constructor(
        private readonly shopCartService: ShopCartService,
        private readonly store: Store,
        private readonly uiService: UiService,
        private readonly itemProductCartService: ItemProductCartService,
        private readonly productsService: ProductsService,
        private readonly staticResourceService: StaticResourceService,
        private readonly translationService: TranslationService,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {
    }

    public ngOnChanges(changes): void {
        const quantity = { value: this.item.quantity, key: this.item.quantity };
        if (changes.item?.previousValue && quantity.value !== this.quantitySelected.value) {
            this.getItemPricesWithDiscountKey();
        }
        this.quantitySelected = quantity;
        this.hasStock = this.item.availability === CartConstants.IN_STOCK && this.item.quantity <= this.item.unitsAvailable;
        this.setOptionsAccordingType();
        if (this.showMessageStock && !this.hasStock) {
            // NOTE: No se están lanzando acciones de redux, no podemos abstraer esta analítica
            this.itemProductCartService.stockErrorAnalytics(this.item, this.type.name);
        }
    }

    public ngOnInit(): void {
        if (this.item.stockLocations?.length > 0 && 'preSaleType' in this.item.stockLocations[0]) {
            this.showPreSaleKey = true;
        }

        this.hasStockAllSizes = this.item.unitsAvailable > 0;

        this.showStockOrigin$ = this.store.select(getCmsConfigValue('enable_message_item_from_shop'))
            .pipe(
                first(),
                map(value => value && this.item.stockLocations?.some((item) => item.stockSourceCode.includes('PST'))),
            );

        if (!this.hasStockAllSizes) {
            this.inactiveClass = 'type-inactive';
        }

        this.isCustomization = this.item.customization?.length > -1;
        this.isGift = this.item.reference.toUpperCase().includes(CartConstants.XGIFT);
        this.isPatch = this.item.reference.toUpperCase().includes(CartConstants.PATCHES_REFERENCE);
        this.isCard = this.item.productType
            === CartProductTypes.PHYSICAL_CARD || this.item.productType === CartProductTypes.VIRTUAL_CARD;

        const { selectedSize, oneSize, sizeName } = this.itemProductCartService.setSizeValues(this.item);

        this.selectedSize = selectedSize;
        this.oneSize = oneSize;
        this.sizeName = sizeName;

        this.optionsSizes$ = combineLatest([
            this.store.select(getShopCartItemSizesFactory(this.item.sku)),
            this.store.select(getShopCartItemsStocks),
        ]).pipe(
            map(([sizes, stocks]) => {
                // Remove sizes if stock property availability is not in_stock
                if (sizes && stocks) {
                    return sizes.filter(size => stocks?.[size?.value]?.availability === CartConstants.IN_STOCK);
                }
            }),
        );

        this.quantityOptions = this.itemProductCartService.getQuantityValues();

        this.date = DatesService.toLocaleString(this.item?.createdAt);

        if (this.item.color) {
            this.colorName = this.item.color.charAt(0).toUpperCase()
                + this.item.color.slice(1).toLowerCase();
        } else if (this.item.colorName) {
            this.colorName = this.item.colorName;
        }

        // Esto podria simplificarse simplemente con los itemType ya que incluyen items de tipo Gift, de tipo Card, Autoadd y personalizable pero hay que probarlo bien
        this.isEditable = !this.isCard && !this.isGift && this.itemTypeEditable && !this.item.isAutoAdd;

        this.setOptionsAccordingType();
        const widthImage = this.window.innerWidth / (this.uiService.ui.device.mobile ? 2 : 5);
        this.itemImagesUrl$ = this.shopCartService
            .getImageItem(
                this.item,
                widthImage,
            );
        this.getItemPricesWithDiscountKey();

        this.actions$ = this.store.select(getCmsConfigValue('enable_wishlist'))
            .pipe(
                take(1),
                map(enableWish => {
                    switch (this.type?.name) {
                        case CartItemTypes.ITEM_MINI_CART:
                            if (enableWish
                                && (!isDefined(this.item?.customization) || this.item?.customization?.length === 0)
                                && !this.isCard) {
                                return OPTIONS_CART_WISH;
                            }
                            return OPTIONS_CART;
                        case CartItemTypes.ITEM_SHOP_CART:
                            if (
                                (!isDefined(this.item?.customization) || this.item?.customization?.length === 0)
                                && !this.isCard) {
                                if (enableWish) {
                                    return OPTIONS_CART_WISH;
                                }
                                return OPTIONS_CART_BUYLATER;
                            }
                            return OPTIONS_CART;
                        case CartItemTypes.ITEM_BUYLATER:
                            return OPTIONS_WISH;
                        case CartItemTypes.ITEM_MINI_BUYLATER:
                            if (enableWish) {
                                return OPTIONS_WISH;
                            }
                            break;
                        default:
                            return OPTIONS_DEFAULT;
                    }
                })
            );

        this.isLastUnits$ = this.store.select(getShopCartItemsStocks).pipe(
            filter(stocks => !!stocks),
            map(stocks => stocks[this.item.sku]?.typeThreshold === lowThreshold),
        );
    }

    public getItemPricesWithDiscountKey() {
        this.itemPrices$ = this.shopCartService.getPricesItem(this.item, this.type, this.preventMultiplyQuanity).pipe(
            switchMap((prices) => {
                if (prices.secondaryText) {
                    return this.translationService.getKeyWithDateConfig(prices.secondaryText).pipe(
                        map((key) => {
                            return {
                                ...prices,
                                secondaryText: key,
                            };
                        })
                    );
                }
                return of(prices);
            })
        );
    }

    public onSelectQuantity(quantity): void {
        this.selectQuantity.emit({
            item: this.item,
            quantity,
        });
    }

    public onSelectSize(sku): void {
        this.selectSize.emit({
            item: this.item,
            size: this.itemProductCartService.getSizeSelected(this.item, parseInt(sku)),
        });
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    public actionItem() {
        if (!this.isPatch) {
            // GA4 ANALYTICS // TODO: No se están lanzando acciones de redux, no podemos abstraer esta analítica
            this.store.dispatch(GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.select_item,
                eventFrom: ['item-buylater', 'item-mini-buylater'].includes(this.type.name) ? EventFrom.wishList : EventFrom.shopcart,
                data: {
                    items: [{ ...this.item }],
                },
            }));
            switch (this.type.name) {
                case CartItemTypes.ITEM_GIFT_TICKET:
                    break;
                case CartItemTypes.ITEM_MINI_CART:
                case CartItemTypes.ITEM_SHOP_CART:
                    // Si es un producto regalo autoañadido no linkara a nada
                    // Si no existe stock en ninguna de las tallas, abrir modal
                    if (this.item.isAutoAdd && this.item.isGiftableProduct) {
                        break;
                    } else if (!this.hasStock && !this.item.colors.find(color => color.id === this.item.colorId)
                        .sizes.find(sizes => sizes.availability === CartConstants.IN_STOCK)) {
                        this.openNoStockModal();
                    } else {
                        this.itemProductCartService.goToProduct(this.isCard, this.item, true, this.type.name);
                    }
                    break;
                case CartItemTypes.ITEM_MINI_BUYLATER:
                case CartItemTypes.ITEM_BUYLATER:
                    this.itemProductCartService.goToProduct(
                        this.isCard,
                        { ...this.item, parentId: this.item.id },
                        false,
                        this.type.name,
                    );
                    break;
                case CartItemTypes.ITEM_MY_ACCOUNT:
                    this.itemProductCartService.goToProduct(this.isCard, this.item, false, this.type.name);
                    break;
                default:
                    break;
            }
        }
    }

    public launchEvent(action: ItemProductCartButtonActions, item: CartItem): void {
        // TODO ECOMDUTI-21029 repasar con la wish
        // action.changeWithStock ? (hasStockAllSizes ? action.event : 'none') : action.event, item
        if (action.changeWithStock && !this.hasStockAllSizes && action.event !== 'deleteItemShopcart') {
            this.selectAction.emit({ action: 'none', item });
        }
        this.selectAction.emit({ action: action.event, item });
    }

    public trackByAction(index: number, action: any): number {
        return action.event;
    }

    /**
     * Función para abrir diversas modales en función de varias casuísticas
     */
    public openModals(): void {
        switch (this.type.name) {
            case CartItemTypes.ITEM_SHOP_CART:
                this.openNoStockModal();
                break;
            default:
                this.itemProductCartService.seeShopDisponibility(this.item);
        }
    }

    public openCompositionModal(): void {
        this.store.dispatch(LoadCompositionModal({ productId: this.item.parentId + '', colorId: this.item.colorId }));
    }

    private setOptionsAccordingType(): void {
        switch (this.type.name) {
            case CartItemTypes.ITEM_GIFT_TICKET:
                this.showSize = true;
                this.showQuantity = true;
                this.showQuantityEdit = true;
                this.showMessageStock = false;
                this.quantityOptions = this.itemProductCartService.getQuantityValues(this.item.quantity);
                break;
            case CartItemTypes.ITEM_MINI_CART:
            case CartItemTypes.ITEM_SHOP_CART:
                this.showQuantityEdit = this.isEditable;
                this.showSizeEdit = this.isEditable && !this.oneSize;
                this.showMessageStock = true;
                break;
            case CartItemTypes.ITEM_BUYLATER:
                this.showSize = false;
                this.showQuantity = false;
                break;
            case CartItemTypes.ITEM_POPUP_ADD_TO_CART:
                this.showActions = false;
                this.showQuantity = false;
                this.showDisplayReference = false;
                this.preventMultiplyQuanity = true;
                break;
            case CartItemTypes.ITEM_ORDER_RETURN:
                this.showDate = true;
                if (this.isCustomization) {
                    this.inactiveClass = 'type-inactive';
                }
                break;
            case CartItemTypes.ITEM_RETURN_STATUS:
                this.isGift = false;
                this.showColor = false;
                this.showColorName = true;
                this.showSize = true;
                this.showQuantity = true;
                this.showActions = false;
                this.showDisplayReference = false;
                this.showReference = true;
                this.showQuantityEdit = false;
                this.showSizeEdit = false;
                this.showMessageStock = false;
                this.isEditable = false;
                this.showPreSaleKey = false;
                this.showDate = false;
                this.enableClick = false;
                break;
            case CartItemTypes.ITEM_ORDER_SUMMARY:
                this.showActions = false;
                this.showQuantity = false;
                this.showDisplayReference = false;
                break;
            default:
                break;
        }
        this.setOptionsDefault();
    }

    private setOptionsDefault(): void {
        // opciones para cuando el item es una tarjeta
        if (this.isCard) {
            this.showQuantity = false;
            this.showColor = false;
            this.showSize = false;
            this.showDisplayReference = false;
            this.showMessageStock = false;
        }
        // opciones para cuando el item es una caja regalo
        if (this.isGift) {
            this.showActions = false;
            this.showSize = false;
            this.showMessageStock = false;
            this.showColor = false;
        }
        // opciones para cuando el item es producto regalado
        if (this.item.isAutoAdd) {
            this.showActions = false;
            this.showQuantity = false;
            this.showDisplayReference = false;
        }
        // opciones para cuando es un producto personalizable
        if (this.isCustomization) {
            this.showSizeEdit = false;
        }
    }

    /**
     * Función para abrir la modal de producto agotado
     */
    private openNoStockModal(): void {
        this.productsService.productDetail(`${this.item.parentId}`, '', this.item.colorId).pipe(first()).subscribe((product: Product) => {
            this.store.dispatch(LoadNoStockModal({ product }));
        });
    }
}
